/**
 * Client-side image helpers for inline base64 storage.
 * We downscale and re-encode as JPEG so a 5 MB phone photo becomes ~80–200 KB
 * — small enough to ship as a data-URI without bloating the document.
 */

const DEFAULT_MAX_DIM = 800;
const DEFAULT_QUALITY = 0.85;

export async function fileToCompressedDataUri(
    file,
    { maxDim = DEFAULT_MAX_DIM, quality = DEFAULT_QUALITY } = {},
) {
    if (!file) return null;
    if (!file.type.startsWith("image/")) {
        throw new Error("Only image files are supported.");
    }
    const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error("Could not read this image."));
        i.src = URL.createObjectURL(file);
    });
    const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * ratio));
    const h = Math.max(1, Math.round(img.height * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(img.src);
    return canvas.toDataURL("image/jpeg", quality);
}
